import { Component, h, Host, Prop, Element, Build, Watch } from '@stencil/core';
import Splide, { type Options } from '@splidejs/splide';
import type {
  CmsInpageNavigationConfig,
  SplideComponentConfig,
} from '../../helpers/utils/interfaces';
import { updateNextArrowState } from '../../helpers/splide-helper';

@Component({
  tag: 'cms-inpage-navigation',
  styleUrl: 'cms-inpage-navigation.scss',
  shadow: true,
})
export class CmsInpageNavigation {
  @Element() host: HTMLElement;

  /**
   * Config of the navigation as JSON-String.
   */
  @Prop({ mutable: true }) config: string;

  /**
   * Set the tracking namespace (optional)
   */
  @Prop({ mutable: true }) namespace?: string;

  /**
   * Set the translations for splide
   */
  @Prop() translations: string;

  private static readonly SCROLL_OFFSET = 160;

  private options: CmsInpageNavigationConfig;
  private splideTranslations: SplideComponentConfig;
  private splideNode: HTMLElement;
  private splide: Splide;
  private container: HTMLDivElement;
  private isApp: boolean;
  private targets: HTMLElement[];

  private readonly splideConfig = {
    type: 'slide',
    perMove: 1,
    gap: '0',
    autoWidth: true,
    slideFocus: false,
    pagination: false,
    trimSpace: true,
    role: 'carousel',
  } as Options;

  private readonly stickyObserverConfig = {};
  private readonly headingObserverConfig = {
    rootMargin: '0px 0px -70% 0px',
  };
  // observe container-observed for sticky tracking this.container
  private stickyObserver: IntersectionObserver;
  // observe is visible hb-global-footer-bottombar-container for hidden this.container
  private footerObserver: IntersectionObserver;
  // observe is visible scroll target subheadline
  private headingObserver: IntersectionObserver;
  private navigationIndex: string;

  private targetId: string;

  @Watch('config')
  private configWatcher(newValue: string | CmsInpageNavigationConfig) {
    if (newValue) {
      this.options =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
    }
  }

  connectedCallback() {
    if (this.config === undefined) {
      return;
    }
    this.configWatcher(this.config);
  }

  componentWillLoad() {
    if (this.translations) {
      this.splideTranslations = JSON.parse(this.translations);
    }
  }

  componentDidLoad() {
    this.splideNode = this.host.shadowRoot.querySelector('.splide');
    this.container = this.host.shadowRoot.querySelector('.container');
    this.isApp = document.querySelector('.cms-app') !== null;
    if (!Build.isBrowser) {
      return;
    }
    this.mountSplide();
    this.updateTargets();

    this.addIntersectionObserver();
  }

  private addStickyObserver() {
    const containerObserved = this.host.shadowRoot.querySelector(
      '.container-observed',
    );
    try {
      this.stickyObserver = new IntersectionObserver(
        this.isStickyIntersection,
        this.stickyObserverConfig,
      );

      this.stickyObserver?.observe(containerObserved);
    } catch (e) {
      console.error(e);
    }
  }

  private addFooterObserved() {
    const footerObserved = document.querySelector(
      '.hb-global-footer-bottombar-container',
    );

    try {
      this.footerObserver = new IntersectionObserver(this.footerIsIntersection);
    } catch (e) {
      console.error(e);
    }

    this.footerObserver?.observe(footerObserved);
  }

  private addHeadingObserver() {
    try {
      this.headingObserver = new IntersectionObserver(
        this.headingIsIntersection,
        this.headingObserverConfig,
      );
    } catch (e) {
      console.error(e);
    }

    for (const target of this.targets) {
      this.headingObserver.observe(target);
    }
  }

  private addIntersectionObserver() {
    if (this.options.sticky) {
      this.addStickyObserver();
      this.addFooterObserved();
    }
    this.addHeadingObserver();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly isStickyIntersection = (
    entries: IntersectionObserverEntry[],
  ) => {
    if (entries) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const entry of entries) {
        const currentlyActive = this.getCurrentlyActiveLink();
        if (entry.intersectionRatio === 0) {
          this.container?.classList.add('nav-container-sticky');
        } else {
          this.container?.classList.remove('nav-container-sticky');
        }
        if (entry.boundingClientRect.top < window.scrollY && !this.isApp) {
          currentlyActive?.classList.remove('active');
        }
      }
    }
  };

  private readonly footerIsIntersection = (
    entries: IntersectionObserverEntry[],
  ) => {
    if (entries) {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.container?.classList.add('hidden');
        } else {
          this.container?.classList.remove('hidden');
        }
      }
    }
  };

  private readonly headingIsIntersection = (
    entries: IntersectionObserverEntry[],
  ) => {
    if (entries) {
      const currentlyActive = this.getCurrentlyActiveLink();
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.handleIntersection(entry, currentlyActive);
        }
      }
    }
  };

  private handleIntersection(
    entry: IntersectionObserverEntry,
    currentlyActive: Element | undefined,
  ) {
    const { id } = entry.target;
    const shouldBeActiveNode = this.getShouldBeActiveLink(id) as HTMLElement;
    const { navigationIndex } = shouldBeActiveNode?.dataset || undefined;
    if (this.navigationIndex) {
      if (this.targetId === id) {
        this.goSliderIndex(navigationIndex);
        this.navigationIndex = null;
      }
    } else {
      this.goSliderIndex(navigationIndex);
    }
    currentlyActive?.classList.remove('active');
    shouldBeActiveNode?.classList.add('active');
  }

  private getCurrentlyActiveLink() {
    return this.host.shadowRoot.querySelector('.active') || undefined;
  }

  private getShouldBeActiveLink(id: string) {
    return id
      ? this.host.shadowRoot.querySelector(
          `.inpage-link[data-scroll-id="${id}"]`,
        )
      : null;
  }

  private goSliderIndex(index: string) {
    const indexNum = Number.parseInt(index) || 0;
    const { go } = this.splide.Components.Controller;
    const { prev } = this.splide.Components.Arrows.arrows;
    const firstItem = this.splide.Components.Elements.slides[0];
    if (firstItem.classList.contains('is-visible')) {
      prev.classList.add('disabled');
    } else {
      prev.classList.remove('disabled');
    }
    go(indexNum);
  }

  private updateTargets() {
    const { items } = this.options || undefined;
    if (items) {
      this.targets = Array.from(
        items
          .map((item) => document.getElementById(item.scrollId))
          .filter((element) => element !== null),
      );
    }
  }

  private mountSplide() {
    const { length } = this.options.items || undefined;
    if (length <= 0) {
      return;
    }
    this.splide = new Splide(this.splideNode, this.splideConfig);
    this.splide.on('ready resized visible moved', () =>
      updateNextArrowState(this.splide),
    );
    this.splide.mount();
  }

  private scrollToElement(event: Event): void {
    event.preventDefault();
    const { currentTarget } = event || undefined;
    const { dataset } = (currentTarget as HTMLAnchorElement) || undefined;
    const { scrollId, navigationIndex } = dataset || undefined;
    if (!(scrollId && navigationIndex)) {
      return;
    }
    this.navigationIndex = navigationIndex;
    this.targetId = scrollId;
    const node = document.getElementById(scrollId);
    window.scrollTo({
      top: this.getElementYOffset(node),
      behavior: 'smooth',
    });
  }

  private getElementYOffset(node: HTMLElement) {
    const offsetTop = this.getOffsetTop(node) || 0;
    let position = 0;
    if (offsetTop !== 0) {
      position = offsetTop - CmsInpageNavigation.SCROLL_OFFSET;
    }
    return Math.max(0, position);
  }

  private getOffsetTop(node: HTMLElement) {
    if (!node) return 0;

    let offsetTop = 0;
    let element: HTMLElement = node;
    while (element) {
      offsetTop += element.offsetTop;
      element = element.offsetParent as HTMLElement;
    }
    return offsetTop;
  }

  render() {
    return (
      <Host>
        <div class='container-observed' />
        <section
          class={`wrapper container ${
            this.options.sticky ? 'container-sticky' : ''
          }`}
        >
          <div class='splide wrapper-inner'>
            <div class='splide__slider slider'>
              <div class='splide__arrows slider-arrows-wrapper'>
                <cms-button
                  namespace={`${this.namespace}-arrow-left`}
                  class={
                    ' splide__arrow splide__arrow--prev splide-keyvisual-navi-arrow-prev'
                  }
                  theme='secondary'
                  size='button-xs'
                  type='button'
                  icon='arrowLeft'
                  focusable={false}
                  icon-size='sm'
                  tag-title={this.splideTranslations?.arrowLeftLabel}
                />
                <cms-button
                  namespace={`${this.namespace}-arrow-right`}
                  class={
                    'splide__arrow splide__arrow--next splide-keyvisual-navi-arrow-next'
                  }
                  theme='secondary'
                  size='button-xs'
                  type='button'
                  icon='arrowRight'
                  focusable={false}
                  icon-size='sm'
                  tag-title={this.splideTranslations?.arrowRightLabel}
                />
              </div>
              <div class='splide__track splide-keyvisual-navi-track'>
                <ul class='splide__list splide-keyvisual-navi-list'>
                  {this.options.items.map((item, idx) => (
                    <li
                      key={'1'}
                      class='splide__slide splide-keyvisual-navi-slide'
                    >
                      <cms-link
                        data-eventtype='click'
                        data-event='cta_click'
                        data-uid='cms-inpage-navigation'
                        data-scroll-id={item.scrollId}
                        data-navigation-index={idx}
                        onClick={(event: Event) => {
                          this.scrollToElement(event);
                        }}
                        class='inpage-link link'
                        type='standalone-inverted'
                        href={`#${item.scrollId}`}
                        content={item.text}
                        size='sm'
                        linkTitle={item.text}
                        target={item.target}
                        nowrap={true}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      </Host>
    );
  }
}
